// @flow

import React, { useState, useRef, lazy, type Node } from 'react';
import enablePointerEvents from 'app/common/react/components/PinRep/utils/enablePointerEvents';
import SafeSuspense from 'app/packages/safe-suspense';
import Saved from 'app/common/react/components/PinRep/components/Saved';
import useTrackingCallback from 'app/common/react/components/PinRep/Hooks/useTrackingCallback';
import { Box } from 'gestalt';
import { Experiment } from 'app/common/react/components/experiments';
import { type DetailedBoard } from 'app/packages/pinterest-flow-types/board';
import { type PersistentOneClickSaveOptions } from 'app/common/react/components/FeedsAndFollowing/Homefeed/MoreIdeasTabsNav/PersistentOneClickSave';
import { type Pin } from 'app/common/react/components/PinRep/types';
import { type SaveTarget } from 'app/common/redux/reducers/pin';
import { useTrackingParameters } from 'app/common/react/components/PinRep/TrackingParametersContext';

const PinRepEditButton = lazy(() =>
  import(/* webpackChunkName: "PinRepEditButtonCommon" */ /* webpackPrefetch: true */ 'app/common/react/components/PinRep/components/PinRepEditButton'),
);
const PersistentOneClickSave = lazy(() =>
  import(/* webpackChunkName: "PinPersistentOneClickSaveCommon" */ 'app/common/react/components/FeedsAndFollowing/Homefeed/MoreIdeasTabsNav/PersistentOneClickSave'),
);
const PinSaveButton = lazy(() =>
  import(/* webpackChunkName: "PinSaveButtonCommon" */ /* webpackPrefetch: true */ 'app/common/react/components/save/PinSaveButton/PinSaveButton'),
);
const PinBetterSave = lazy(() =>
  import(/* webpackChunkName: "PinBetterSaveCommon" */ /* webpackPrefetch: true */ 'app/common/react/components/common/PinBetterSave/PinBetterSave'),
);
// Note(jackhsu): we do not prefetch this because it is less likely for this component to be necessary
const BoardActivityPinRepLikeButton = lazy(() =>
  import(/* webpackChunkName: "BoardActivityPinRepLikeButtonCommon" */ 'app/common/react/components/BoardActivities/BoardActivityPinRepLikeButton'),
);
const PinPromoteButton = lazy(() =>
  import(/* webpackChunkName: "PinPromoteButtonCommon" */ /* webpackPrefetch: true */ 'app/common/react/components/DeprecatedPinRep/PinPromoteButton'),
);

// SaveButtonOptions dictates which save button should be shown on the Pin and
// how it should behave:
// - none: no save button shown
// - classic: button, shows board picker modal
// - controlled: button, one-tap save
// - inline: PinBetterSave button/dropdown
// - persistentOneClickSave: persistent "plus" icon to save, this mirrors inApp experiences for more ideas surfaces
export type RenderPinSaveTooltipFunction = (ref?: HTMLElement) => Node;
export type SaveButtonOptions =
  | {| type: 'none' |}
  | {| type: 'classic', renderPinSaveTooltip?: RenderPinSaveTooltipFunction |}
  | {|
      type: 'controlled',
      save: () => void,
      saved: boolean,
      renderPinSaveTooltip?: RenderPinSaveTooltipFunction,
    |}
  | {|
      type: 'inline',
      initialSelection?: SaveTarget,
      onSave?: () => void,
      renderPinSaveTooltip?: RenderPinSaveTooltipFunction,
    |}
  | PersistentOneClickSaveOptions
  | {| type: 'promote', renderPinSaveTooltip?: RenderPinSaveTooltipFunction |};

export type Props = {|
  pin: Pin,
  board?: DetailedBoard,

  // Allow custom save button options. These will be derived from pin and
  // viewerBoardCount otherwise (see getSaveButtonOptions).
  derivedSaveButtonOptions: SaveButtonOptions,

  // Used to force a hover experience. See PinRepContainer for more context.
  onToggleFlyout: (showToggle: boolean) => void,
  onToggleModal: (showModal: boolean) => void,

  // Imperative props handed down from PinRepConfiguration
  showEditButton: boolean,
  showLikeButton: boolean,
  supportsReactionBar: boolean,
|};

// PinRepPrimaryContent displays the primary actionable buttons for the PinRep,
// displayed at the top of the PinRep.
export default function PinRepPrimaryContent({
  derivedSaveButtonOptions,
  onToggleFlyout,
  onToggleModal,
  pin,
  board,
  showEditButton,
  showLikeButton,
  supportsReactionBar,
}: Props) {
  const [showTopLeftButtonOnly, setShowTopLeftButtonOnly] = useState(false);
  const { carousel_data: carouselData, id: pinId, savedInfo } = pin;
  const initialSavedInfoRef = useRef(!!savedInfo);

  const { viewParameter, viewType } = useTrackingParameters();
  const handleSaveButtonClick = useTrackingCallback(PCONST.thrift.EventType.CLICK, {
    element: PCONST.thrift.ElementType.PIN_REPIN_BUTTON,
    objectId: pinId,
  });

  // Choose which primary button we should use based on derivedSaveButtonOptions
  let primaryButton = null;

  const editPinProps = {
    onToggleModal: onToggleFlyout,
    view: viewType,
    viewParameter,
    useLegoStyles: true,
  };

  if (!showTopLeftButtonOnly) {
    if (
      derivedSaveButtonOptions.type === 'classic' ||
      derivedSaveButtonOptions.type === 'controlled'
    ) {
      const { renderPinSaveTooltip } = derivedSaveButtonOptions;

      primaryButton = (
        <Box display="flex" justifyContent="end">
          <SafeSuspense>
            <Box data-test-id="pin-save-button">
              {enablePointerEvents(
                <PinSaveButton
                  icon={false}
                  onClickCallback={handleSaveButtonClick}
                  onToggleModal={onToggleModal}
                  oneTapSave={
                    derivedSaveButtonOptions.type === 'controlled' && derivedSaveButtonOptions
                  }
                  pin={pin}
                  renderPinSaveTooltip={renderPinSaveTooltip}
                  variant="lego"
                />,
              )}
            </Box>
          </SafeSuspense>
        </Box>
      );
    } else if (derivedSaveButtonOptions.type === 'inline') {
      // TODO: Fix these types.
      const pinAny: any = pin;

      const { initialSelection, onSave, renderPinSaveTooltip } = derivedSaveButtonOptions;

      primaryButton = (
        <SafeSuspense>
          <Box data-test-id="better-save">
            {enablePointerEvents(
              <PinBetterSave
                initialSelection={initialSelection}
                onSave={onSave}
                onToggleFlyout={onToggleFlyout}
                onToggleModal={onToggleModal}
                pin={pinAny}
                renderPinSaveTooltip={renderPinSaveTooltip}
                renderSavedStatus={({ savedInfo }) =>
                  savedInfo &&
                  enablePointerEvents(
                    <Saved
                      isNewlySaved={!initialSavedInfoRef.current}
                      name={savedInfo.title}
                      url={savedInfo.url}
                    />,
                  )
                }
                useMasonryFlyout
                variant="lego-pin"
                viewParameter={viewParameter}
              />,
            )}
          </Box>
        </SafeSuspense>
      );
    } else if (derivedSaveButtonOptions.type === 'persistentOneClickSave') {
      primaryButton = (
        <SafeSuspense>
          <Box data-test-id="persistent-one-click-save">
            {enablePointerEvents(
              <PersistentOneClickSave
                {...editPinProps}
                saveOptions={derivedSaveButtonOptions}
                pin={pin}
              />,
            )}
          </Box>
        </SafeSuspense>
      );
    } else if (derivedSaveButtonOptions.type === 'promote') {
      primaryButton = (
        <Box display="flex" justifyContent="end">
          <SafeSuspense>
            <Box data-test-id="pin-promote-button">
              {enablePointerEvents(<PinPromoteButton pin={pin} />)}
            </Box>
          </SafeSuspense>
        </Box>
      );
    }
  }

  let topLeftButton = null;

  if (showEditButton) {
    // Own boards add an edit button. The rest will leave it off.
    topLeftButton = (
      <SafeSuspense>
        {enablePointerEvents(
          <Box data-test-id="edit-button" display="inlineBlock">
            <PinRepEditButton
              {...editPinProps}
              carouselSlotCurrentIndex={carouselData && carouselData.index}
              component={PCONST.thrift.ComponentType.FLOWED_PIN}
              pinId={pinId}
              style={{ size: 'sm' }}
            />
          </Box>,
        )}
      </SafeSuspense>
    );
  } else if (showLikeButton) {
    // Group boards add a like button. The rest will leave it off.
    topLeftButton = (
      <SafeSuspense>
        <Box data-test-id="like-button">
          <Experiment name="group_boards_reactions" activate={false}>
            {({ anyEnabled: inReactionsExperiment, customActivate: activateReactionsExperiment }) =>
              enablePointerEvents(
                <BoardActivityPinRepLikeButton
                  pin={pin}
                  board={board}
                  activateReactionsExperiment={activateReactionsExperiment}
                  inReactionsExperiment={inReactionsExperiment}
                  onShowReactionsBar={() => setShowTopLeftButtonOnly(true)}
                  onHideReactionsBar={() => setShowTopLeftButtonOnly(false)}
                  supportsReactionBar={supportsReactionBar}
                  useLegoStyles
                />,
              )
            }
          </Experiment>
        </Box>
      </SafeSuspense>
    );
  }

  return (
    <Box display="flex" margin={3}>
      {topLeftButton && (
        <Box
          dangerouslySetInlineStyle={{
            __style: { marginRight: 'auto' },
          }}
          flex="grow"
        >
          {topLeftButton}
        </Box>
      )}
      {primaryButton && (
        <Box
          dangerouslySetInlineStyle={{
            __style: { marginLeft: 'auto' },
          }}
          flex="grow"
          overflow="hidden"
        >
          {primaryButton}
        </Box>
      )}
    </Box>
  );
}
